import { Box } from "@mui/material";
import { DotLegalDialog, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import LinkedControlsBox from "../../policy/policySections/LinkedControlsBox";
import { resolveControls } from "../../policy/policySections/PolicySections.utils";
import { useSelectableControlsForRisk } from "../riskControls/RiskControls.hooks";

interface CompleteMitigationPlanDialogProps {
    frameworkControlIds: Array<string>;
    inProgress: boolean;
    onClose: () => void;
    onSubmit: () => void;
}

// Completing a plan graduates its controls onto the risk's standing coverage, so the one-click status change
// becomes a confirmation that discloses that side effect — and shows each control's task-rolled-up status chip,
// so a control the roll-up says is not done is visible at the exact moment the plan is declared finished.
// Only reached when the plan has controls; a control-less plan completes directly with no dialog.
function CompleteMitigationPlanDialog({ frameworkControlIds, inProgress, onClose, onSubmit }: CompleteMitigationPlanDialogProps) {
    const { translateString } = useTranslation();

    // Chips render only for resolvable ids: controls on archived frameworks or excluded controls are absent
    // from the selectable list, but the graduation itself is unaffected.
    const controlsQuery = useSelectableControlsForRisk();
    const controls = resolveControls(frameworkControlIds, controlsQuery.data ?? []);

    return (
        <DotLegalDialog
            open
            header={translateString("completeMitigationPlanHeader")}
            buttonOkText={translateString("riskMitigationStatusCompleted")}
            onDialogClose={onClose}
            onOkClick={onSubmit}
            inProgress={inProgress}
            isLoading={controlsQuery.isLoading}
            size={"sm"}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <DotLegalHeader headerStyle="small" fontWeight={400} marginBottom={0}>
                    {translateString("completeMitigationPlanControlsInfo")}
                </DotLegalHeader>
                <LinkedControlsBox controls={controls} heading="controlsAddedByThisPlan" />
            </Box>
        </DotLegalDialog>
    );
}

export default CompleteMitigationPlanDialog;
